import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { message, Alert, Button, List, Modal, Select } from 'antd'
import { updateCustomParent } from '../../../../modules/materialCustom'

const materialLabel = (material) =>
  `${material.name}${
    material.commonName ? ` (${material.commonName})` : ''
  } · หน่วย ${material.unit}`

// ย้ายวัสดุที่ร้านสร้างไปผูกกับวัสดุมาตรฐานตัวใหม่ ทีละรายการหรือหลายรายการ (spec 017 D3–D5)
const ReparentModal = ({ visible, rows, materials, onClose }) => {
  const dispatch = useDispatch()
  const [parentMaterialId, setParentMaterialId] = useState(undefined)
  const [isSaving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (visible) {
      setParentMaterialId(undefined)
      setResult(null)
    }
  }, [visible, rows])

  const target = (materials || []).find(
    (material) => material.id === parentMaterialId
  )
  const mismatched = target
    ? rows.filter((row) => row.unit !== target.unit)
    : []

  const close = () => {
    // มีรายการที่ย้ายสำเร็จแล้ว ตารางต้องโหลดใหม่แม้บางรายการจะไม่สำเร็จ
    onClose(!!result && result.succeeded > 0)
  }

  const onSubmit = async () => {
    setSaving(true)
    let succeeded = 0
    const failed = []
    // เรียกทีละรายการ เพื่อให้รายการที่ผ่านสำเร็จ และรู้เหตุผลของรายการที่ไม่ผ่าน
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index]
      // eslint-disable-next-line no-await-in-loop
      const response = await dispatch(
        updateCustomParent(row.id, parentMaterialId)
      )
      if (response.success) {
        succeeded += 1
      } else {
        failed.push({ id: row.id, name: row.name, reason: response.message })
      }
    }
    setSaving(false)

    if (failed.length === 0) {
      message.success(`ย้ายที่ผูกสำเร็จ ${succeeded} รายการ`)
      onClose(true)
      return
    }
    setResult({ succeeded, failed })
  }

  const resultFooter = (
    <Button type="primary" onClick={close}>
      ปิด
    </Button>
  )

  const submitFooter = (
    <div>
      <Button onClick={close} disabled={isSaving}>
        ยกเลิก
      </Button>
      <Button
        type="primary"
        loading={isSaving}
        disabled={!parentMaterialId}
        onClick={onSubmit}>
        ย้ายที่ผูก
      </Button>
    </div>
  )

  return (
    <Modal
      title={
        rows.length > 1 ? `ย้ายที่ผูก ${rows.length} รายการ` : 'ย้ายที่ผูก'
      }
      visible={visible}
      footer={result ? resultFooter : submitFooter}
      closable={!isSaving}
      maskClosable={false}
      onCancel={close}>
      <List
        size="small"
        style={{ marginBottom: 16, maxHeight: 180, overflowY: 'auto' }}
        dataSource={rows}
        renderItem={(row) => (
          <List.Item>
            <span>
              <b>{row.name}</b> ·{' '}
              {row.ownerHeroName || `Hero ${row.ownerHeroId}`}
            </span>
            <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              ผูกกับ {row.parentMaterialName || '-'} · หน่วย {row.unit}
            </span>
          </List.Item>
        )}
      />
      {!result && (
        <div>
          <div style={{ marginBottom: 8 }}>วัสดุมาตรฐานตัวใหม่</div>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="เลือกวัสดุมาตรฐาน (พิมพ์ชื่อเพื่อค้น)"
            value={parentMaterialId}
            onChange={setParentMaterialId}
            disabled={isSaving}
            filterOption={(input, option) =>
              String(option.props.children)
                .toLowerCase()
                .includes(input.toLowerCase())
            }>
            {(materials || []).map((material) => (
              <Select.Option key={material.id} value={material.id}>
                {materialLabel(material)}
              </Select.Option>
            ))}
          </Select>
          {mismatched.length > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message={`หน่วยไม่ตรงกับวัสดุที่เลือก ${mismatched.length} รายการ ระบบจะไม่ย้ายรายการเหล่านั้น`}
            />
          )}
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="มีผลกับรายการที่ชั่งหลังจากนี้เท่านั้น รายการเดิมไม่เปลี่ยน"
          />
        </div>
      )}
      {result && (
        <Alert
          type={result.succeeded > 0 ? 'warning' : 'error'}
          showIcon
          message={`สำเร็จ ${result.succeeded} รายการ · ไม่สำเร็จ ${result.failed.length} รายการ`}
          description={
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              {result.failed.map((item) => (
                <li key={item.id}>
                  {item.name}: {item.reason}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </Modal>
  )
}

ReparentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  rows: PropTypes.array.isRequired,
  materials: PropTypes.array,
  onClose: PropTypes.func.isRequired
}

ReparentModal.defaultProps = {
  materials: []
}

export default ReparentModal
